import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { UnsupportedMediaTypeError } from 'activate-errors';
import { createAndUploadMedia } from 'lane-shared/helpers/createAndUploadMedia';
import { dataUriToBlob } from 'lane-shared/helpers/files';
import { LibraryType } from 'lane-shared/types/libraries/LibraryType';
import { MediaDocumentContentTypeEnum, MediaTypeEnum } from 'constants-media';
import {
  isBase64MediaPayload,
  type Base64MediaPayload,
} from 'application-types';

import { H3 } from 'components/typography';

import { createThumbnail, enforceSize } from '../helpers/images';
import type { FileReturnType } from 'helpers/fileReaderResolver';
import { getMediaType } from 'helpers/media/getMediaType';
import type { Media } from 'graphql-query-contracts';
import {
  DocumentMediaOptions,
  HTMLImage,
  ImageMediaOptions,
  ThumbnailType,
} from 'lane-shared/types/media';
import { AmazonS3Buckets } from 'lane-shared/config';

type Props = {
  onMediaCreated: (media: Media) => void;
  mediaOptions?: ImageMediaOptions | DocumentMediaOptions;
  selectedLibrary?: LibraryType | null;
  path?: string | null;
  tags?: string[] | null;
  s3Bucket?: string;
};

type FullMediaForUpload = {
  media: HTMLImage | File | Base64MediaPayload;
  mediaBlob: Blob;
  thumbnail?: ThumbnailType;
  thumbnailBlob?: Blob;
};

export default function useMediaUpload({
  selectedLibrary,
  onMediaCreated,
  mediaOptions,
  tags,
  path,
  s3Bucket = AmazonS3Buckets.Lane,
}: Props) {
  const { t } = useTranslation();

  const [uploading, setUploading] = useState(false);

  async function filesSelectedHandler(files: FileReturnType[]) {
    setUploading(true);

    await window.Alert.loading({
      title: t('Uploading…'),
      children: <H3 style={{ textAlign: 'center' }}>0 / {files.length}</H3>,
    });

    for (let i = 0; i < files.length; i++) {
      const file = files[i]!;

      try {
        await uploadMedia(file);
      } catch (error) {
        console.error(error);
        window.Toast.show(
          <H3 style={{ textAlign: 'center' }}>
            {t('Failed to upload')}{' '}
            <span>{typeof file !== 'string' && file.name}</span>
          </H3>
        );
      }

      await window.Alert.loading({
        title: t('Uploading…'),
        children: (
          <H3 style={{ textAlign: 'center' }}>
            {i + i} / {files.length}
          </H3>
        ),
      });
    }

    setUploading(false);
    window.Alert.hide();
  }

  const processMediaForUpload = async (
    media: FileReturnType,
    mediaType: MediaTypeEnum
  ): Promise<FullMediaForUpload> => {
    let fullMediaForUpload: FullMediaForUpload;

    switch (mediaType) {
      case MediaTypeEnum.Image: {
        // HEIC decode/resize/EXIF happens server-side (worker thread), so
        // skip the client-side canvas resize entirely and pass the raw
        // base64 straight through.
        if (isBase64MediaPayload(media)) {
          fullMediaForUpload = {
            media,
            // Never actually uploaded — createMedia already wrote the file
            // to S3 server-side and returns fileSignedUrl: null for this
            // case, which createAndUploadMedia uses to skip the PUT.
            mediaBlob: new Blob(),
          };
          break;
        }

        const image = media as HTMLImage;
        const resized = enforceSize({
          image,
          ...mediaOptions,
          quality: 1,
        });
        const resizedBlob = dataUriToBlob(resized.uri);
        const thumbnail = createThumbnail({ image });
        const thumbnailBlob = dataUriToBlob(thumbnail.uri);

        fullMediaForUpload = {
          media: image,
          mediaBlob: resizedBlob,
          thumbnail,
          thumbnailBlob,
        };
        break;
      }

      case MediaTypeEnum.Document: {
        const document = media as File;

        fullMediaForUpload = {
          media: document,
          mediaBlob: document,
        };
        break;
      }

      default:
        throw new UnsupportedMediaTypeError();
    }

    return fullMediaForUpload;
  };

  const uploadMedia = async (media: FileReturnType) => {
    if (!selectedLibrary) return;

    const mediaType: MediaTypeEnum = getMediaType(media);

    // currently we only support PDF documents.
    if (
      mediaType === MediaTypeEnum.Document &&
      (media as File).type !== MediaDocumentContentTypeEnum.pdf
    ) {
      throw new UnsupportedMediaTypeError();
    }

    const mediasAndThumbnail = await processMediaForUpload(media, mediaType);
    const newMedia = await createAndUploadMedia({
      selectedLibrary,
      name: (media as File).name,
      ...mediasAndThumbnail,
      type: mediaType,
      path,
      tags,
      s3Bucket,
      _id: media?._id,
    });

    onMediaCreated(newMedia);
  };

  return {
    filesSelectedHandler,
    uploading,
  };
}
